// Step 7 commit 7 / AC212-AC214: server sync. Pure helpers for
// flattening the legacy Cart to ServerCartItem[], merging local +
// remote items on sign-in (additive: sum bunchCount across matching
// keys), and materializing the merged items back into a Cart by
// replaying addToBox.

import type { Cart, CartLine } from "./types";
import type { ServerCartItem } from "./server-types";
import type { Variety } from "@/lib/catalog/types";
import { addToBox, consolidateBuckets } from "./cart";
import { EMPTY_CART } from "./types";

// One line key per { slug, lengthCm, colorKey, unit }. Two adds of the
// same variety at the same length + color + unit sum into one item.
function lineKey(item: {
  slug: string;
  lengthCm: number | null;
  colorKey: string | null;
  unit: "bunch" | "box" | "stem";
  boxCode?: string;
}): string {
  // 12.8: for BOX-mode lines the box size is part of the identity. A QB and an
  // HB of the same variety are DIFFERENT products (125 vs 300 stems); merging
  // them under one key summed their counts while keeping only the first item's
  // stems-per-unit, which is how a box-mode line silently changed price.
  // Bunch-mode lines keep the original key - the packer, not the buyer, picks
  // their box, so the box size is not part of what was bought.
  const box = item.unit === "box" ? `::${item.boxCode ?? "-"}` : "";
  return `${item.slug}::${item.lengthCm ?? "-"}::${item.colorKey ?? "-"}::${item.unit}${box}`;
}

export function cartToServerItems(
  cart: Cart,
  now: Date = new Date(),
): ServerCartItem[] {
  // 12.8: collapse lines that are the SAME product into one item.
  //
  // Box mode now emits one CartLine per physical box (that is what gives each
  // box its own identity), so 3 QB of Freedom are three lines. Flattened
  // naively they became three ServerCartItems -> three invoice lines, each
  // labelled "Box 1-3" with Qty 125. Alexis's rule: ONE line, "Box 1-3
  // Freedom", Qty 375 = 3 boxes x 125 stems, priced on that total.
  //
  // Same-key lines always carry the same stems-per-unit (a QB is always 125),
  // so summing the counts is exact - and the key includes the box size, so a
  // QB and an HB of one variety never collapse together.
  const merged = new Map<string, ServerCartItem>();
  const order: string[] = [];
  for (const box of cart.boxes) {
    for (const line of box.contents) {
      const item = lineToServerItem(line, now);
      const key = lineKey(item);
      const existing = merged.get(key);
      if (existing) {
        existing.bunchCount += item.bunchCount;
      } else {
        merged.set(key, item);
        order.push(key);
      }
    }
  }
  return order.map((k) => merged.get(k)!);
}

function lineToServerItem(
  line: CartLine,
  now: Date,
): ServerCartItem {
  // stemsPerBunch on the server is the stems-per-unit-of-sale:
  // 25 for a rose bunch, 125 for a QB rose box, 100 for an EB rose
  // box, 20 for a hydrangea box, etc. Always derive from
  // stems / quantity so the meaning matches both unit branches.
  //
  // The previous `line.stemsPerBunch ?? fallback` short-circuit
  // returned the variety's per-bunch value even when unit="box",
  // causing every box-unit rose order to persist at one-fifth of
  // the displayed total (5 bunches per QB -> 25 stems per box ->
  // 1 box * 25 * pricePerStem). Bug surfaced as `$22.25` totals on
  // multi-bunch rose orders that the cart UI was correctly showing
  // as `$111.25`. Fixed 2026-06-04.
  const stemsPerBunch =
    line.stems > 0 && line.quantity > 0
      ? line.stems / line.quantity
      : (line.stemsPerBunch ?? 0);
  return {
    slug: line.slug,
    variety: line.variety,
    category: line.category,
    lengthCm: line.length ?? null,
    colorKey: line.colorChoice ?? null,
    unit: line.unit,
    bunchCount: line.quantity,
    stemsPerBunch,
    pricePerStem: line.pricePerStem,
    // #468 STABLE ORDER: preserve the line's ORIGINAL add time (fall back to now
    // only for a legacy line without one). Re-materialize used to stamp `now`,
    // which reshuffled boxes on every mutation - the top complaint. A stable
    // addedAt lets canonicalizeCart keep each box in its position.
    addedAt: line.addedAt ?? now.toISOString(),
    // 12.8 MONEY FIX: carry the purchased box size so the replay rebuilds the
    // box the buyer actually bought instead of guessing the smallest one.
    boxCode: line.boxCode,
    // #468 SIZE DRIVES PACKING: carry the group's chosen box size so the server
    // packer (invoice + freight) fills to the same size the client chose.
    groupBoxSize: line.groupBoxSize,
  };
}

// PASTE #363: order-independent fingerprint of a cart's items. Detects whether
// the local cart is a SYNCED copy of the server cart (a reload) vs genuine
// unsynced guest additions, so a page load never doubles the cart.
export function hashCartItems(
  items: Pick<
    ServerCartItem,
    "slug" | "unit" | "lengthCm" | "colorKey" | "bunchCount"
  >[],
): string {
  return items
    .map(
      (i) =>
        `${i.slug}|${i.unit}|${i.lengthCm ?? "-"}|${i.colorKey ?? "-"}|${i.bunchCount}`,
    )
    .sort()
    .join(",");
}

// PASTE #363 (BUG C): decide what a page-load hydrate should do. The old boot
// SUM-merged local + remote every time; since local is a copy of the server
// cart, each reload DOUBLED it (282 -> 606 -> 1008) and an impersonated client's
// cart bled into the admin session. Here the server cart is authoritative on
// boot; local items are ADOPTED (merged) only when they are genuine unsynced
// guest additions - local non-empty AND not matching the sync marker stored last
// sync. A reload / cross-device load / impersonation leftover matches the marker
// (or is empty) -> load remote, never re-merge. Idempotent.
export function resolveBootCart(
  local: ServerCartItem[],
  remote: ServerCartItem[],
  syncedMarker: string | null,
): { merged: ServerCartItem[]; adopted: boolean } {
  const localIsSynced =
    local.length === 0 || hashCartItems(local) === syncedMarker;
  const merged = localIsSynced ? remote : mergeServerItems(local, remote);
  const adopted =
    !localIsSynced && hashCartItems(merged) !== hashCartItems(remote);
  return { merged, adopted };
}

// #532 (lost-add race): a background sync builds `base` from a `snapshot` of
// the local cart, awaits the network, then writes. Anything the buyer changed
// locally during that await (`fresh` vs `snapshot`) must be folded back in,
// or it is silently overwritten. Per line key: base + (fresh - snapshot).
// Additions add, removals subtract, a line that reaches 0 is dropped, and a
// line new to `base` is taken from `fresh`. When nothing changed locally the
// result is `base` unchanged.
export function rebaseLocalEdits(
  base: ServerCartItem[],
  snapshot: ServerCartItem[],
  fresh: ServerCartItem[],
): ServerCartItem[] {
  const snap = new Map(snapshot.map((i) => [lineKey(i), i]));
  const now = new Map(fresh.map((i) => [lineKey(i), i]));
  const out = new Map(base.map((i) => [lineKey(i), { ...i }]));
  for (const key of new Set([...snap.keys(), ...now.keys()])) {
    const delta = (now.get(key)?.bunchCount ?? 0) - (snap.get(key)?.bunchCount ?? 0);
    if (delta === 0) continue;
    const existing = out.get(key);
    if (existing) {
      const count = existing.bunchCount + delta;
      if (count > 0) out.set(key, { ...existing, bunchCount: count });
      else out.delete(key);
    } else if (delta > 0) {
      out.set(key, { ...now.get(key)!, bunchCount: delta });
    }
  }
  return Array.from(out.values());
}

export function mergeServerItems(
  local: ServerCartItem[],
  remote: ServerCartItem[],
): ServerCartItem[] {
  const map = new Map<string, ServerCartItem>();
  for (const item of remote) {
    map.set(lineKey(item), { ...item });
  }
  for (const item of local) {
    const key = lineKey(item);
    const existing = map.get(key);
    if (existing) {
      map.set(key, {
        ...existing,
        bunchCount: existing.bunchCount + item.bunchCount,
        // Prefer the freshest addedAt + price snapshot.
        addedAt:
          new Date(item.addedAt) > new Date(existing.addedAt)
            ? item.addedAt
            : existing.addedAt,
        pricePerStem: item.pricePerStem,
      });
    } else {
      map.set(key, { ...item });
    }
  }
  return Array.from(map.values());
}

export function materializeCart(
  items: ServerCartItem[],
  varietyLookup: Map<string, Variety>,
): Cart {
  let cart: Cart = EMPTY_CART;
  for (const item of items) {
    const variety = varietyLookup.get(item.slug);
    if (!variety) continue;
    try {
      const outcome = addToBox(
        cart,
        {
          variety,
          length: item.lengthCm ?? undefined,
          unit: item.unit,
          quantity: item.bunchCount,
          colorChoice: item.colorKey ?? undefined,
          // 12.8 MONEY FIX: replay the box the buyer bought. Omitting this
          // silently downsized every box-mode line to the smallest box.
          boxCode: item.boxCode,
          // #468 STABLE ORDER: carry the ORIGINAL add time through the replay so
          // box ordering (by earliest addedAt) is stable across re-materialize.
          addedAt: item.addedAt,
          // #468 SIZE DRIVES PACKING: replay the group's chosen size so the
          // packer fills to it (client cart == invoice == freight).
          groupBoxSize: item.groupBoxSize,
        },
        { forceAccept: true },
      );
      if (outcome.kind === "added") {
        cart = outcome.cart;
      }
    } catch (err) {
      console.warn(`[cart-sync] skipping unmaterialized item ${item.slug}:`, err);
    }
  }
  // #430: consolidate any bucket whose incremental replay spilled into a lone
  // trailing box (e.g. #4686 fillers). Only reduces box counts, never merges
  // across buckets, never changes capacities - so segregation (#353) + parity
  // (#384) hold and non-affected orders are byte-identical.
  return consolidateBuckets(cart, varietyLookup);
}
